import {
  Scissors,
  TreePine,
  CircleDot,
  Flame,
  ClipboardCheck,
  AlertTriangle,
  Building,
  Calendar,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const iconMap = {
  scissors: Scissors,
  axe: TreePine,
  "circle-dot": CircleDot,
  flame: Flame,
  "clipboard-check": ClipboardCheck,
  "alert-triangle": AlertTriangle,
  building: Building,
  calendar: Calendar,
};

const services = [
  {
    id: "tree-trimming",
    title: "Premium Tree Trimming",
    description:
      "Expert pruning and shaping to maintain your trees' health, beauty, and property value. Our skilled trimmers bring extensive expertise.",
    icon: "scissors",
    featured: true,
  },
  {
    id: "tree-removal",
    title: "Safe Tree Removal",
    description:
      "Professional removal of dangerous, diseased, or unwanted trees with complete safety protocols.",
    icon: "axe",
  },
  {
    id: "stump-removal",
    title: "Stump Grinding",
    description:
      "Complete stump removal leaving your landscape clean and ready for new projects.",
    icon: "circle-dot",
  },
  {
    id: "fire-safety",
    title: "Fire Safety & Brush Removal",
    description:
      "Protect your property from wildfire risk with defensible space creation.",
    icon: "flame",
  },
  {
    id: "arborist",
    title: "ISA Arborist Assessment",
    description:
      "Certified professional tree health diagnosis. Next-day appointments available.",
    icon: "clipboard-check",
  },
  {
    id: "emergency",
    title: "24/7 Emergency Service",
    description: "Immediate response for storm damage and monsoon emergencies.",
    icon: "alert-triangle",
  },
  {
    id: "hoa-commercial",
    title: "HOA & Commercial",
    description: "Large-scale property management for communities and hotels.",
    icon: "building",
  },
];

export default function ServicesSection() {
  return (
    <section
      id="services"
      className="py-28 lg:py-36 bg-[#FDFCF9]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-4">
            Comprehensive Tree Care
          </p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A1628] mb-6">
            Expert Services for
            <br />
            <span className="gradient-text">Elite Properties</span>
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto text-lg">
            From fire safety to luxury estate maintenance, we provide expert
            solutions for the most discerning property owners in Paradise Valley
            and Scottsdale.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Large Featured Card - Tree Trimming */}
          <div className="lg:col-span-2 lg:row-span-2 luxury-card group relative overflow-hidden min-h-[500px] flex flex-col justify-end p-0">
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
              style={{
                backgroundImage: `url('https://images.pexels.com/photos/18355077/pexels-photo-18355077.jpeg?auto=compress&cs=tinysrgb&w=800')`,
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/60 to-transparent" />
            <div className="relative z-10 p-8 lg:p-10">
              <div className="inline-flex items-center gap-2 bg-[#10B981] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider mb-6">
                <Scissors className="w-4 h-4" />
                Featured Service
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-3xl lg:text-4xl font-bold text-white mb-4">
                Premium Tree Trimming
              </h3>
              <p className="text-white/80 text-base lg:text-lg mb-6 max-w-lg">
                Expert pruning and shaping to maintain your trees' health,
                beauty, and property value. Our ISA certified team brings
                extensive expertise to every job.
              </p>
              <Button
                asChild
                className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-none px-8 py-4 uppercase tracking-[0.15em] text-xs font-bold group/btn"
                data-testid="service-trimming-cta"
              >
                <a href="#contact" className="flex items-center gap-3">
                  Request Service
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </a>
              </Button>
            </div>
          </div>

          {/* Other Service Cards */}
          {services.slice(1).map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <div
                key={service.id}
                className="luxury-card group p-8 flex flex-col"
                data-testid={`service-card-${service.id}`}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#10B981]/20 to-[#10B981]/5 flex items-center justify-center mb-6 group-hover:bg-gradient-to-br group-hover:from-[#10B981] group-hover:to-[#059669] transition-all duration-300">
                  <IconComponent className="w-7 h-7 text-[#10B981] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#0A1628] mb-3">
                  {service.title}
                </h3>
                <p className="text-[#64748B] text-sm flex-grow mb-4">
                  {service.description}
                </p>
                <a
                  href="#contact"
                  className="text-[#10B981] text-sm font-bold uppercase tracking-wider hover:text-[#059669] transition-colors inline-flex items-center gap-2 group/link"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                </a>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-20 text-center">
          <p className="text-[#64748B] mb-6">
            Not sure which service you need? Our ISA certified arborists will
            provide a complimentary consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-none px-10 py-5 uppercase tracking-[0.15em] text-sm font-bold shadow-lg shadow-emerald-500/20"
              data-testid="services-cta-call"
            >
              <a href="tel:4803517775">Call (480) 351-7775</a>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-2 border-[#0A1628] text-[#0A1628] hover:bg-[#0A1628] hover:text-white rounded-none px-10 py-5 uppercase tracking-[0.15em] text-sm font-bold"
              data-testid="services-cta-consult"
            >
              <a href="#contact">Free Consultation</a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
